/**
 * ⟦la-sesion-de-la-spa⟧ `R2-UI6` · **QUIÉN ESTÁ CONECTADO, UNA SOLA VEZ, Y QUÉ
 * PASA CUANDO DEJA DE ESTARLO.** `// ADR-0353`
 *
 * `PRD_R2.md:2175-2186` (quién está conectado), `:2188-2207` (el orden de las
 * pantallas de «acá no hay nada»), Reglas 6, 19, 20, 23 y 24 (`:2299-2322`).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ TRES COSAS QUE ESTE MÓDULO **NO** HACE, Y CADA UNA ES UN CRITERIO
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   · **No guarda NADA en `localStorage`** (ni en `sessionStorage`, ni en una
 *     cookie propia). `CA-F6-53`: *«la sesión es la cookie; la SPA no guarda nada
 *     en `localStorage`: `quienSoy()` al cargar es la única fuente»*. Recargar el
 *     navegador vuelve a preguntar, y por eso la baja de `CA-F6-57` tiene efecto:
 *     una copia local del actor sobreviviría a la baja y mostraría una consola
 *     que el servidor ya no sirve.
 *   · **No intercepta el `409` para decidir la contraseña.** Regla 6 y `P9`: *«la
 *     guardia de la SPA se apoya en quién soy»*. El `mustChoosePassword` sale del
 *     cuerpo de `quienSoy()`. El `409` que llegue de un borde **no decide nada
 *     por sí solo** (`R2-UI10`): los contratos declaran 409 de negocio con la
 *     misma forma de cuerpo, así que se vuelve a preguntar `quienSoy()`, como
 *     con el 403, y la marca —o su ausencia— decide la fase.
 *   · **No decide qué se dibuja.** Publica el estado; el orden de las cuatro
 *     pantallas lo escribe `App.tsx` (`Rutas`), que es el único lugar desde donde
 *     se ven las cuatro juntas.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⚠️ POR QUÉ ES UN CONTEXTO Y POR QUÉ **NO** SE LLAMA `contexto-sesion.tsx`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * El precedente vivo de esta app es **pasar por PROP** y no agregar un contexto:
 * `App.tsx` lo deja escrito tres veces (`cargarElegibles`, `bordePadron`,
 * `bordeCarga`) con su motivo — *«`gates/pantalla-envios.spec.ts` DESCUBRE los
 * contextos barriendo `src/contexto-*.tsx`: agregar uno cambia lo que ese arnés
 * monta»*.
 *
 * ⛔ **Acá la prop no alcanza, y es medible:** el bloque de sesión va al PIE DEL
 * RAIL (Regla 23), y el rail se monta desde `MarcoDelBrazo`/`MarcoTransversal`,
 * que `react-router` instancia como `element=` de una `<Route>` — o sea **no hay
 * cómo pasarle una prop desde `Rutas` sin envolver las dos y volver a
 * prop-drillear cuatro niveles**, en dos marcos y en la barra de Inicio.
 *
 * ⇒ **contexto, pero el archivo se llama `sesion.tsx` y no `contexto-sesion.tsx`**,
 * que es el nombre que `r2-ui6.md:70` ya pedía. El barrido de ese arnés es
 * `src/contexto-*.tsx`; este archivo **no matchea**, así que lo que ese examen
 * monta no cambia. La elección es mía y está declarada para que se pueda revertir
 * barato: renombrarlo sería el cambio, no reescribir el módulo.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import type { JSX, ReactNode } from "react"

import type { ForbiddenStatus } from "@contracts/identity/http.ts"
import type { Role } from "@contracts/identity/index.ts"

import {
  ESTADO_DEBE_ELEGIR,
  ESTADO_SIN_SESION,
  type ActorDeLaSesion,
  type BordeSesion,
} from "./login/index.ts"
import { registrarReaccionDeSesion } from "./mensaje-de-error.ts"

// ════════════════════════════════════════════════════════════════════════════
// LOS CÓDIGOS · literal a mano + alias de tipo del contrato
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⭐ **403 — la sesión es válida, el rol no alcanza.** `// ADR-0353` §D2.
 *
 * ⛔ **El molde es el que `R2-UI5` dejó sancionado** en
 * `login/borde-sesion.ts:84-87`: literal escrito a mano + **alias de TIPO** del
 * contrato. `IDENTITY_STATUS` (`contracts/identity/http.ts:84`) es un `const`
 * OBJETO ⇒ importarlo es un import de VALOR desde `contracts/`, que **pasa `tsc`
 * y rompe `vite build`** (`vite.config.ts:10-16`, `ADR-0330`). El alias
 * `ForbiddenStatus` (`http.ts:587`) es `typeof IDENTITY_STATUS.forbidden`: si el
 * contrato mueve el 403, **esta línea deja de compilar**.
 *
 * ⚠️ Los otros dos —401 y 409— **no se vuelven a escribir**: se importan de
 * `src/login/`, que ya los tiene con este mismo molde y los reexporta
 * (`borde-sesion.ts:232-234`). Un import de valor **de la SPA**, no de
 * `contracts/`. Tres literales del mismo número en dos archivos es exactamente lo
 * que este molde viene a evitar.
 */
const ESTADO_DENEGADO: ForbiddenStatus = 403

// ════════════════════════════════════════════════════════════════════════════
// EL ROL, Y SU NOMBRE EN PANTALLA
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⭐ **Cómo se llama cada rol EN LA PANTALLA.** `CA-F6-48`, `CA-F6-49`,
 * `CA-F6-50`: el bloque de sesión dice «Administrador» / «Acompañante».
 *
 * ⛔ **El copy vive acá y no en el contrato, y eso lo fija el contrato mismo**:
 * `contracts/identity/index.ts:77` dice, textual, que *«el nombre de la persona
 * —“Acompañante”, con tilde y mayúscula— es copy de pantalla»*. El valor del
 * cable es `acompanante`, sin tilde; el de la pantalla lleva tilde. Son dos cosas
 * distintas y por eso hay una tabla.
 *
 * ⛔⛔ **`satisfies Record<Role, string>` y no `: Record<Role, string>`**, y la
 * diferencia es lo que sostiene el validador de abajo: con `satisfies`, el objeto
 * conserva sus claves literales —así `hasOwnProperty` decide sobre el conjunto
 * REAL— **y además** `tsc` exige que estén las dos de `ROLE_VALUES`. El día que el
 * contrato agregue un rol, este objeto deja de compilar y alguien tiene que
 * contestar cómo se llama en pantalla — que es justo la pregunta que un rol nuevo
 * obliga a hacerse.
 */
const ETIQUETA_DEL_ROL = {
  administrador: "Administrador",
  acompanante: "Acompañante",
} satisfies Record<Role, string>

/** El nombre en pantalla de un rol. `CA-F6-48`…`CA-F6-50`. */
export function etiquetaDelRol(rol: Role): string {
  return ETIQUETA_DEL_ROL[rol]
}

/**
 * ⭐ **El validador del borde: `SessionResponseBody.role` es `string`.**
 *
 * El contrato lo tipa `string` a propósito (`http.ts:456`): *«tiparlo como `Role`
 * acá sería afirmar que lo que llegó por la red ya es válido»*. La validación
 * canónica es `roleId()` (`contracts/identity/index.ts:148`) y **es una función ⇒
 * import de VALOR ⇒ no se puede usar acá** (`ADR-0330`). Este es el mismo
 * desenlace escrito con lo que la SPA sí puede importar.
 *
 * ⚠️ **Devuelve `null` en vez de lanzar**, al revés que `roleId`: un rol que esta
 * consola no conoce **no es una excepción, es una sesión que no se puede ubicar**
 * — no hay a dónde mandarla, y `PRODUCT.md` §Principios ① prohíbe dejar a alguien
 * mirando una pantalla que no le corresponde. Se trata como «sin sesión», que es
 * la falla CERRADA. Ver `interpretar()`.
 *
 * ⚠️ El `as Role` está inmediatamente debajo del `hasOwnProperty` que lo justifica
 * y es el único de este archivo: no hay forma de estrechar un `string` a una unión
 * de literales sin uno, y ponerlo pegado al chequeo es lo que lo vuelve legible.
 */
export function rolDeLaSesion(valor: string): Role | null {
  return Object.prototype.hasOwnProperty.call(ETIQUETA_DEL_ROL, valor) ? (valor as Role) : null
}

// ════════════════════════════════════════════════════════════════════════════
// EL ESTADO
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⭐ **Las cuatro fases, y ninguna es «no sé».**
 *
 * ⛔ `consultando` **NO es lo mismo que `sin-sesion`**, y colapsarlas es el modo
 * de falla más caro de esta etapa: mientras `quienSoy()` viaja, la SPA todavía no
 * sabe nada, y tratar eso como «no hay sesión» mandaría al login —reescribiendo
 * la barra— a alguien que sí la tiene. `CA-F6-53` (la sesión sobrevive a recargar)
 * falla exactamente así, y **falla intermitente**: sólo cuando la red tarda.
 */
export type EstadoDeSesion =
  | { readonly fase: "consultando" }
  | { readonly fase: "sin-sesion" }
  | { readonly fase: "debe-elegir" }
  | { readonly fase: "abierta"; readonly actor: ActorDeLaSesion; readonly rol: Role }

export interface Sesion {
  readonly estado: EstadoDeSesion
  /** Vuelve a preguntar `quienSoy()`. Lo usa la reacción al `403` (`CA-F6-55`). */
  refrescar(): void
  /** Regla 24 · `salir()` del borde, **sin `window.confirm`**. */
  cerrar(): Promise<void>
}

const ContextoDeSesion = createContext<Sesion | null>(null)

/**
 * La sesión, para quien esté adentro del `ProveedorDeSesion`.
 *
 * ⛔ **Tira si no hay proveedor** en vez de devolver «sin sesión»: un componente
 * montado fuera del proveedor que leyera «sin sesión» dibujaría una consola
 * anónima sin que nada falle. El error nombra el módulo, que es lo accionable.
 */
export function useSesion(): Sesion {
  const sesion = useContext(ContextoDeSesion)
  if (sesion === null) {
    throw new Error("`useSesion()` fuera de `<ProveedorDeSesion>` (src/sesion.tsx).")
  }
  return sesion
}

/**
 * ⭐⭐ **EL PROVEEDOR: `quienSoy()` UNA VEZ AL CARGAR, Y LA REACCIÓN CENTRAL A
 * 401/403/409.** `// ADR-0353`
 *
 * ⛔ **«Una vez» es literal**: el efecto no depende de la ruta ni del render, sólo
 * del borde. Una consulta por navegación sería un pedido a `auth.<sitio>` por cada
 * clic del menú, y `PRD:2175` pide saber quién está conectado, no vigilarlo.
 * Quien necesita volver a preguntar lo pide (`refrescar()`), y los que lo piden
 * son el `403` de `CA-F6-55` y el `409` (`R2-UI10`: resolver si es la marca de
 * Regla 6 o un 409 de negocio).
 */
export function ProveedorDeSesion({
  borde,
  children,
}: {
  readonly borde: BordeSesion
  readonly children: ReactNode
}): JSX.Element {
  const [estado, setEstado] = useState<EstadoDeSesion>({ fase: "consultando" })

  /**
   * ⛔ **El montaje vivo, para no tocar estado de un componente desmontado.** En
   * `StrictMode` —que `main.tsx` usa— React monta, desmonta y vuelve a montar cada
   * efecto en desarrollo; sin esto, la primera consulta vuelve sobre una instancia
   * muerta y React avisa por consola. En las pruebas el síntoma es peor: un
   * `setState` fuera de `act()` que ensucia el caso SIGUIENTE.
   */
  const vivo = useRef(true)

  const consultar = useCallback(async (): Promise<void> => {
    try {
      const actor = await borde.quienSoy()
      if (!vivo.current) return
      setEstado(interpretar(actor))
    } catch {
      /*
       * ⛔⛔ **UN BORDE CAÍDO NO ES «SIN SESIÓN», Y ACÁ SE DECIDE QUE SÍ LO ES.**
       *
       * `borde-sesion.ts` ya separa los dos casos —`quienSoy()` devuelve `null`
       * SÓLO con un `401`, y *«cualquier otro fallo se propaga: colapsarlos en
       * `null` mandaría al login a alguien que sí tiene sesión»*—. Acá llega ese
       * «cualquier otro fallo»: `auth.<sitio>` caído, CORS, DNS, un 500.
       *
       * ⚠️ **Se colapsa a `sin-sesion` igual, y es una ELECCIÓN MÍA, no del spec.**
       * Las dos lecturas:
       *
       *   ① como está · el operador ve la pantalla de login. Es la falla CERRADA:
       *      nunca se dibuja una consola sin saber quién la mira. Lo que se pierde
       *      es el diagnóstico — un `identity-web` caído se lee como «me
       *      deslogueó», que es un reporte de bug equivocado.
       *   ② una quinta fase `no-se-pudo-preguntar` con su propio cartel. Es MEJOR
       *      para operar y **ningún CA del PRD la pide**: `PRD:2188-2207` enumera
       *      TRES pantallas de «acá no hay nada» más el orden 0, y ésta sería una
       *      cuarta sin criterio que la mida.
       *
       * ⇒ ① , por `ADR-0319` (lo que ningún criterio nombra no se construye) y
       * porque es la que falla cerrada. **Queda anotada para que se pueda revertir
       * barato**: es este `catch` y una rama más en `Rutas`.
       */
      if (!vivo.current) return
      setEstado({ fase: "sin-sesion" })
    }
  }, [borde])

  useEffect(() => {
    vivo.current = true
    void consultar()
    return () => {
      vivo.current = false
    }
  }, [consultar])

  /**
   * ⭐⭐ **LA REACCIÓN CENTRAL, REGISTRADA EN `mensaje-de-error.ts`.**
   * `CA-F6-55` (403) y `CA-F6-57` (401). Ver ahí la medición de cobertura: **20
   * de 20 ramas de error HTTP de los trece bordes pasan por esa función**, y las
   * dos exenciones están declaradas.
   *
   * ⛔ **Efecto aparte y no adentro del de arriba**: son dos ciclos de vida
   * distintos. La consulta se hace una vez; el registro tiene que estar puesto
   * mientras el proveedor viva y **sacado en cuanto se desmonte** — si quedara
   * puesto, un examen que monta dos apps seguidas le mandaría los 401 de la
   * segunda a la primera, que ya no existe.
   */
  useEffect(() => {
    const anterior = registrarReaccionDeSesion((statusCode) => {
      if (!vivo.current) return
      switch (statusCode) {
        case ESTADO_SIN_SESION:
          /*
           * ⛔ `401` de CUALQUIER borde ⇒ `#/login`. `CA-F6-57`: la baja del
           * técnico tiene efecto **en el siguiente pedido**, no en el próximo
           * `quienSoy()`. No se vuelve a preguntar: el 401 YA es la respuesta.
           */
          setEstado({ fase: "sin-sesion" })
          return
        case ESTADO_DEBE_ELEGIR:
          /*
           * ⛔⛔ `R2-UI10` · **un `409` NO es «elegir contraseña» hasta que
           * `quienSoy()` lo diga.** El número solo es ambiguo: el servidor contesta
           * la marca de Regla 6 con la MISMA forma de cuerpo que los 409 de negocio
           * que declaran los contratos (`stateConflict`, `templateAlreadyListed`,
           * `templateNotApproved`, `duplicated`, `recipientOptedOut`). Poner
           * `debe-elegir` acá sacaba al operador a `#/elegir-contrasena` ante un
           * texto de botón repetido (R2-F3-CA-17/31) y le cerraba el panel.
           *
           * ⇒ se resuelve PREGUNTANDO, como el 403: `consultar()` y la fase la
           * decide `interpretar(actor)`, que ya pone `debe-elegir` cuando
           * `actor.mustChoosePassword` (Regla 6 y 9, también si el técnico vuelve
           * a marcar la cuenta con la sesión abierta). Sin la marca, la fase queda
           * `abierta` y la pantalla sigue montada con su cartel.
           *
           * ⛔ NO se compara el texto del cuerpo (prosa del productor, ADR-0201) ni
           * se arma una lista de códigos de negocio a excluir: el próximo 409 que
           * declare un contrato volvería a expulsar al operador.
           *
           * Precio: un `GET` de sesión extra por 409 de negocio, el mismo que paga
           * el 403. Residuo declarado: si `quienSoy()` falla acá, el `catch` de
           * `consultar()` colapsa a `sin-sesion`, igual que con el 403.
           */
          void consultar()
          return
        case ESTADO_DENEGADO:
          /*
           * ⭐⭐ `403` ⇒ **se vuelve a preguntar quién soy, y la pantalla sale
           * sola.** `CA-F6-55`: el técnico le cambia el rol en la base, el
           * siguiente pedido contesta 403, y el enlace de la pantalla de acceso
           * denegado tiene que llevar al destino del rol **NUEVO**.
           *
           * ⛔ **Acá NO se prende ninguna bandera «denegado»**, y ése es el punto
           * fino de este archivo: con el actor refrescado, la dirección donde el
           * operador está parado deja de estar en su zona (`esZonaDelRol`), así
           * que `Rutas` dibuja `AccesoDenegado` **por la misma regla que ya tiene
           * para una dirección escrita a mano** (`CA-F6-35`). Una bandera sería un
           * segundo camino a la misma pantalla, y los dos se separan.
           */
          void consultar()
          return
        default:
          /*
           * Cualquier otro código —400, 404, 500, y el `0` de «no se pudo hablar
           * con el servidor»— **no es de sesión y no se toca**. La pantalla pinta
           * su cartel como siempre.
           */
          return
      }
    })
    return () => {
      registrarReaccionDeSesion(anterior)
    }
  }, [consultar])

  const cerrar = useCallback(async (): Promise<void> => {
    /*
     * ⛔ Regla 24 · **sin `window.confirm`.** `CA-F6-51`: tocar «Cerrar sesión» no
     * abre ningún popup.
     *
     * ⛔ **El estado se pone en `sin-sesion` PASE LO QUE PASE con el pedido.** Un
     * `salir()` que falle por red no puede dejar la consola abierta: lo que el
     * operador pidió es irse. La cookie la sigue teniendo el servidor, y el
     * `quienSoy()` de la próxima carga es el que manda (`CA-F6-52`).
     */
    try {
      await borde.salir()
    } finally {
      if (vivo.current) setEstado({ fase: "sin-sesion" })
    }
  }, [borde])

  const refrescar = useCallback((): void => {
    void consultar()
  }, [consultar])

  return (
    <ContextoDeSesion.Provider value={{ estado, refrescar, cerrar }}>
      {children}
    </ContextoDeSesion.Provider>
  )
}

/**
 * Lo que `quienSoy()` contestó, vuelto fase.
 *
 * ⛔ **El orden importa**: `mustChoosePassword` gana sobre todo lo demás (Regla 6
 * y `CA-F6-16`, *«mientras `mustChoosePassword`, la ÚNICA dirección es
 * `#/elegir-contrasena`»*), y se mira ANTES que el rol — una cuenta recién creada
 * tiene rol válido y aun así no puede ir a ningún lado.
 */
function interpretar(actor: ActorDeLaSesion | null): EstadoDeSesion {
  if (actor === null) return { fase: "sin-sesion" }
  if (actor.mustChoosePassword) return { fase: "debe-elegir" }
  const rol = rolDeLaSesion(actor.role)
  // Un rol que esta consola no conoce no se puede ubicar: falla CERRADA.
  if (rol === null) return { fase: "sin-sesion" }
  return { fase: "abierta", actor, rol }
}

// ════════════════════════════════════════════════════════════════════════════
// ⭐ EL BLOQUE DE SESIÓN · **UN componente, DOS lugares, y dicen LO MISMO**
// ════════════════════════════════════════════════════════════════════════════

/**
 * Regla 23 (PRD:2321) y PRD:2179-2184.
 *
 * ⛔⛔ **Por qué un componente y no dos `<p>`.** Vive en dos lugares —la barra de
 * Inicio y el pie del rail— porque **Inicio no monta el rail** (`App.tsx`: Inicio
 * es la única pantalla sin marco). Dos bloques escritos por separado divergen en
 * cuanto uno agrega el rol y el otro no: `CA-F6-48/49/50` piden nombre + rol +
 * «Cerrar sesión», `CA-F6-46` pide sólo el nombre y `CA-F6-47` el botón. Escrito
 * dos veces, la versión de Inicio se queda sin rol y nadie lo nota hasta que
 * alguien compara las dos pantallas.
 *
 * ⇒ **el bloque es el mismo y dice lo mismo en los dos lados**; el rol se muestra
 * siempre, que es el superconjunto que cumple los cinco criterios.
 *
 * ⚠️⛔ **LA CLASE ES `inicio__sesion` EN LOS DOS LUGARES, Y ES UNA LIMITACIÓN
 * DECLARADA, NO UNA ELECCIÓN DE DISEÑO.** `estilos.css` **no es territorio de
 * `R2-UI6`** (el encargo acota a `src/*.ts(x)`), y `pruebas/r11-clases-sin-regla.test.ts`
 * mide que **ninguna clase del JSX se quede sin regla en la hoja** — o sea, una
 * clase nueva acá pondría más roja una prueba que ya está roja, escondiendo la
 * regresión detrás de un rojo preexistente. Se reusa la única clase que existe y
 * dice lo que este bloque dice. **El pie del rail va a heredar la tipografía de la
 * barra de Inicio y eso probablemente se vea mal**; la regla que falta
 * (`.navegacion__sesion`) queda pedida en el informe, con dueño.
 */
export function BloqueDeSesion(): JSX.Element | null {
  const { estado, cerrar } = useSesion()

  /*
   * Sin sesión abierta no hay bloque: las dos pantallas que se dibujan en esas
   * fases —login y elegir contraseña— no tienen marco ni barra de Inicio, así que
   * esto no se monta ahí. Devolver `null` es más honesto que un «Sesión: —», que
   * es justamente el texto de maqueta que `CA-F6-46` deroga.
   */
  if (estado.fase !== "abierta") return null

  return (
    <p className="inicio__sesion">
      {/*
        ⭐ `CA-F6-46` · «Sesión: Admin Uno» **donde antes decía «Sesión: —»**. El
        nombre sale de `fullName` del cuerpo de `quienSoy()`, que es el mismo valor
        que la fila de auditoría guarda.
      */}
      <span>Sesión: {estado.actor.fullName}</span>
      {/* ⭐ `CA-F6-48`/`49`/`50` · el ROL, con su nombre de pantalla. */}
      <span>{etiquetaDelRol(estado.rol)}</span>
      {/*
        ⭐ Regla 24 · `CA-F6-51` · **un `<button>` y no un enlace.** No es una
        dirección: es una acción con efecto en el servidor. Y sin confirmación:
        *«sale sin preguntar»*.
      */}
      <button type="button" onClick={() => void cerrar()}>
        Cerrar sesión
      </button>
    </p>
  )
}
